import { createSignal, createEffect, onMount } from 'solid-js';
import { Actions } from '../Actions';
import { Action } from '@/types/constants';
import { Logo } from '@/components/Icons';
import { EventTracker } from '@/utils/event-tracker-store';
import { Settings } from '@/types/search';
import classNames from 'classnames';
import { ExpandIcon } from '@/components/Icons/ExpandIcon';
import { CollapseIcon } from '@/components/Icons/CollapseIcon';

export const AnimationDurations = {
  DEFAULT: 500,
  SMALL: 700,
};

export type SlideOverPanelProps = Settings & {
  isOpen: boolean;
  onToggle?: (isOpen: boolean) => void;
  onClose?: () => void;
  panelTitle: string;
  bodyActionButtons?: Action[];
  footerActionButtons?: Action[] | undefined;
  children: any;
  apiHost: string;
  apiKey: string;
  answering: boolean;
  eventTracker?: EventTracker;
  disableModalBackdrop?: boolean;
  defaultExpanded?: boolean;
};

export const SlideOverPanel = (props: SlideOverPanelProps) => {
  const { isOpen, onToggle, onClose, panelTitle, children } = props;
  const [isPanelOpen, setIsPanelOpen] = createSignal(isOpen);
  const [isExpanded, setIsExpanded] = createSignal(
    props.defaultExpanded || false,
  );

  const handleEscape = (e: KeyboardEvent) => {
    if (props.disableModalBackdrop) return;
    if (e.key === 'Escape') {
      if (onToggle) {
        onToggle(false);
      }
      setIsPanelOpen(false);
    }
  };

  const closePanelHandler = () => {
    setIsPanelOpen(false);
    if (onClose) {
      onClose();
    }
    if (onToggle) {
      onToggle(false);
    }
  };

  const stopPropagation = (event: MouseEvent) => {
    event.stopPropagation();
  };

  createEffect(() => {
    setIsPanelOpen(props.isOpen);
  });

  onMount(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    document.addEventListener('keydown', handleEscape, { signal });

    return () => {
      controller.abort();
    };
  });

  const toggleExpand = () => {
    setIsExpanded(!isExpanded());
  };

  return (
    <div
      class={classNames(
        'fixed inset-y-0 right-0 flex max-w-full transition ease-in-out duration-500 sm:duration-700',
        {
          'pointer-events-auto': isPanelOpen() && !props.disableModalBackdrop,
          'pointer-events-none': !isPanelOpen() && !props.disableModalBackdrop,
          'display-block': isPanelOpen(),
          'display-none': !isPanelOpen(),
        },
      )}
      aria-labelledby="slide-over-title"
      role="dialog"
      aria-modal="true"
    >
      {!props.disableModalBackdrop && (
        <div
          class={`fixed inset-0 bg-gray-500 bg-opacity-75 z-1 transition-opacity ${
            isPanelOpen() ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={closePanelHandler}
        />
      )}

      <div class="brainfish-slide-panel__container pointer-events-auto">
        <div
          class={classNames(
            `fixed inset-y-0 right-0 flex max-w-full transition ease-in-out duration-${AnimationDurations.DEFAULT} sm:duration-${AnimationDurations.SMALL} ml-0`,
            {
              'translate-x-0 lg:m-4': isPanelOpen(),
              'translate-x-full lg:m-0 lg:my-4': !isPanelOpen(),
            },
          )}
        >
          <div
            class={`relative w-screen ${isExpanded() ? 'max-w-3xl' : 'max-w-md'} transform rounded-md transition-all duration-300`}
          >
            <div
              class="flex h-full flex-col bg-white shadow-xl rounded-md justify-between"
              on:pointerdown={stopPropagation}
            >
              {/* Header */}
              <div
                class={classNames(
                  'px-6 pt-6 z-10 flex items-center rounded-t-md bfsearch-container bg-[var(--bfsearch-input-button-bg-color)] text-[var(--bfsearch-input-button-color)]',
                )}
                data-testid="bfsearch-container"
              >
                {/* Expand/Collapse button */}
                <button
                  type="button"
                  onClick={toggleExpand}
                  class="relative rounded-md text-[var(--bfsearch-input-buton-color)] hover:text-gray-700 mr-3 hidden md:block"
                  title={isExpanded() ? 'Collapse panel' : 'Expand panel'}
                >
                  <span class="absolute -inset-2.5" />
                  {isExpanded() ? (
                    <CollapseIcon height={22} width={22} fill="currentColor" />
                  ) : (
                    <ExpandIcon height={22} width={22} fill="currentColor" />
                  )}
                </button>
                <h2
                  class="text-base font-semibold leading-6 flex-grow"
                  id="slide-over-title"
                >
                  {panelTitle}
                </h2>
                {/* Close button */}
                <div
                  class={classNames('absolute right-6 top-6 flex', {
                    'opacity-100': isPanelOpen(),
                    'opacity-0': !isPanelOpen(),
                  })}
                >
                  <button
                    type="button"
                    onClick={closePanelHandler}
                    class={classNames(
                      'relative rounded-md text-[var(--bfsearch-input-buton-color)] hover:text-gray-700',
                    )}
                  >
                    <span class="absolute -inset-2.5" />
                    <span class="sr-only">Close panel</span>
                    <svg
                      class="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              {/* BodyActions */}
              <div class="flex flex-col w-full overflow-hidden bg-cover bg-center flex-grow bfsearch-container">
                {children}
                {!props.answering &&
                  props.bodyActionButtons &&
                  props.bodyActionButtons.length > 0 && (
                    <div class="my-4 px-6">
                      <Actions
                        actions={props.bodyActionButtons}
                        apiHost={props.apiHost}
                        apiKey={props.apiKey}
                      />
                    </div>
                  )}
              </div>

              {/* Footer Actions */}
              {!props.footerActionButtons?.length && props.logo?.hide ? null : (
                <div class="flex-shrink-0 px-4 py-4 flex flex-col justify-end border-t border-gray-200 w-full bg-white rounded-b-md bfsearch-container">
                  {!props.answering && (
                    <Actions
                      actions={props.footerActionButtons}
                      apiHost={props.apiHost}
                      apiKey={props.apiKey}
                    />
                  )}
                  <div
                    class={`flex items-center justify-center cursor-pointer ${
                      (props.footerActionButtons?.length ?? 0) > 0 ? 'mt-4' : ''
                    }`}
                  >
                    {props.logo?.hide !== true && (
                      <a
                        href="https://brainfi.sh?utm_source=help-widget"
                        target="_blank"
                        rel="noopener"
                        class="flex items-center"
                      >
                        <span
                          class={`text-gray-500 font-semibold mr-2 ${
                            props.logo?.smaller ? 'text-xs' : 'text-sm'
                          }`.trim()}
                        >
                          Powered by{' '}
                        </span>
                        <Logo
                          class={`w-auto ${
                            props.logo?.smaller ? 'h-5' : 'h-6'
                          }`.trim()}
                          grayscale={props.logo?.monotone}
                        />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlideOverPanel;
